import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Alert,
  Tab,
  Nav,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import GridContasSalario from './GridContasSalario';
import GridHierarquia from './GridHierarquia';
import FormIncluirNivel from './FormIncluirNivel';
import ModalIncluirContaSalario from './ModalIncluirContaSalario';
import ModalSuplente from './ModalSuplente';

const ContasSalario = () => {
  // Estado GE — apenas para habilitar botão "+ Incluir conta" e alimentar modal
  const [grupoEmpresa, setGrupoEmpresa] = useState(null);
  const [opcoesGe, setOpcoesGe] = useState([]);

  // Contas de salário — grid GLOBAL (G-02)
  const [contasSalario, setContasSalario] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);

  // Hierarquia
  const [abaAtiva, setAbaAtiva] = useState('CLT');
  const [hierarquiaClt, setHierarquiaClt] = useState([]);
  const [hierarquiaPj, setHierarquiaPj] = useState([]);
  const [loadingHier, setLoadingHier] = useState(false);

  // Modais
  const [modalIncluirConta, setModalIncluirConta] = useState(false);
  const [modalSuplente, setModalSuplente] = useState(null);

  // Confirmações
  const [confirmConta, setConfirmConta] = useState({ show: false, item: null });
  const [confirmNivel, setConfirmNivel] = useState({
    show: false,
    item: null,
    message: '',
    cascade: false,
  });

  // Carrega GEs do usuário
  useEffect(() => {
    api
      .listarTodosGruposEmpresa()
      .then((res) => {
        const lista = res || [];
        setOpcoesGe(
          lista.map((g) => ({
            value: g.codigo,
            label: g.codigo + ' - ' + (g.apelido || ''),
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Carrega grid global de contas-salário + hierarquia no mount (G-02)
  useEffect(() => {
    carregarContasSalario();
    carregarHierarquia('CLT');
    carregarHierarquia('PJ');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarContasSalario = useCallback(() => {
    setLoadingContas(true);
    api
      .listarContasSalario()
      .then((res) => {
        setContasSalario(res || []);
      })
      .catch(() => {
        toast.error('Erro ao carregar contas-salário.');
      })
      .finally(() => setLoadingContas(false));
  }, []);

  const carregarHierarquia = useCallback((tipo) => {
    setLoadingHier(true);
    api
      .listarHierarquiaContaSalario(tipo)
      .then((res) => {
        const lista = res || [];
        if (tipo === 'CLT') {
          setHierarquiaClt(lista);
        } else {
          setHierarquiaPj(lista);
        }
      })
      .catch(() => {
        toast.error('Erro ao carregar hierarquia ' + tipo + '.');
      })
      .finally(() => setLoadingHier(false));
  }, []);

  const handleExcluirConta = (item) => {
    setConfirmConta({ show: true, item });
  };

  const handleConfirmarExclusaoConta = (confirmado) => {
    const item = confirmConta.item;
    setConfirmConta({ show: false, item: null });
    if (!confirmado || !item) return;

    api
      .excluirContaSalario({
        codigo_grupo_empresa: item.codigo_grupo_empresa,
        codigo_conta: item.codigo_conta,
      })
      .then(() => {
        toast.success('Conta excluída com sucesso.');
        carregarContasSalario();
      })
      .catch((err) => {
        const errMsg = err && err.message ? err.message : 'Erro ao excluir conta.';
        toast.error(errMsg);
      });
  };

  const handleToggleStatus = (item) => {
    const novoStatus = item.status === 'A' ? 'I' : 'A';
    api
      .atualizarStatusHierarquiaContaSalario({
        codigo_usuario: item.codigo_usuario,
        tipo: item.tipo,
        status: novoStatus,
      })
      .then(() => {
        toast.success('Status atualizado.');
        carregarHierarquia(item.tipo);
      })
      .catch((err) => {
        const errMsg = err && err.message ? err.message : 'Erro ao atualizar status.';
        if (errMsg.indexOf('último aprovador ativo') >= 0) {
          toast.error(errMsg);
        } else {
          toast.error(errMsg);
        }
      });
  };

  const handleExcluirNivel = (item) => {
    setConfirmNivel({
      show: true,
      item,
      message: 'Confirma exclusão do usuário "' + item.nome + '"?',
      cascade: false,
    });
  };

  const executarExclusaoNivel = (item, cascade) => {
    api
      .excluirHierarquiaContaSalario({
        codigo_usuario: item.codigo_usuario,
        tipo: item.tipo,
        cascade,
      })
      .then(() => {
        toast.success('Excluído.');
        carregarHierarquia(item.tipo);
      })
      .catch((err) => {
        const errMsg = err && err.message ? err.message : '';
        if (!cascade && errMsg.indexOf('usuário alternativo') >= 0) {
          setConfirmNivel({
            show: true,
            item,
            message: errMsg,
            cascade: true,
          });
        } else if (errMsg.indexOf('último aprovador ativo') >= 0) {
          toast.error(errMsg);
        } else {
          toast.error(errMsg || 'Erro ao excluir.');
        }
      });
  };

  const handleConfirmarExclusaoNivel = (confirmado) => {
    const { item, cascade } = confirmNivel;
    setConfirmNivel({ show: false, item: null, message: '', cascade: false });
    if (!confirmado || !item) return;
    executarExclusaoNivel(item, cascade);
  };

  const handleAdicionarSuplente = (item) => {
    setModalSuplente(item);
  };

  const handleSuccessIncluirConta = () => {
    setModalIncluirConta(false);
    carregarContasSalario();
  };

  const handleSuccessSuplente = () => {
    const tipo = modalSuplente ? modalSuplente.tipo : abaAtiva;
    setModalSuplente(null);
    carregarHierarquia(tipo);
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Contas de Salário" />

      {/* Card Filtros */}
      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <label htmlFor="select-ge">Grupo Empresa</label>
              <Select
                inputId="select-ge"
                options={opcoesGe}
                placeholder="Selecione o Grupo Empresa..."
                isClearable
                value={grupoEmpresa}
                onChange={(opt) => setGrupoEmpresa(opt)}
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Card Contas de Salário — grid GLOBAL */}
      <Card className="shadow-sm mb-3">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Contas de Salário — Visão Global ({contasSalario.length})
          </strong>
          <Button
            variant="light"
            size="sm"
            disabled={!grupoEmpresa}
            onClick={() => setModalIncluirConta(true)}
            title={!grupoEmpresa ? 'Selecione um Grupo Empresa para incluir' : ''}
          >
            + Incluir conta
          </Button>
        </Card.Header>
        <Card.Body>
          <GridContasSalario
            items={contasSalario}
            onExcluir={handleExcluirConta}
            loading={loadingContas}
          />
        </Card.Body>
      </Card>

      {/* Card Hierarquia de Aprovação */}
      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <strong>Hierarquia de Aprovação</strong>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="mb-3" style={{ fontSize: '0.875rem' }}>
            ⚠️ Esta hierarquia é GLOBAL — afeta todos os Grupos Empresa.
          </Alert>
          <Tab.Container
            activeKey={abaAtiva}
            onSelect={(k) => setAbaAtiva(k)}
          >
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="CLT">CLT</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="PJ">PJ</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="CLT">
                <FormIncluirNivel
                  tipo="CLT"
                  onSuccess={() => carregarHierarquia('CLT')}
                />
                <GridHierarquia
                  tipo="CLT"
                  items={hierarquiaClt}
                  onToggleStatus={handleToggleStatus}
                  onAdicionarSuplente={handleAdicionarSuplente}
                  onExcluir={handleExcluirNivel}
                  loading={loadingHier}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="PJ">
                <FormIncluirNivel
                  tipo="PJ"
                  onSuccess={() => carregarHierarquia('PJ')}
                />
                <GridHierarquia
                  tipo="PJ"
                  items={hierarquiaPj}
                  onToggleStatus={handleToggleStatus}
                  onAdicionarSuplente={handleAdicionarSuplente}
                  onExcluir={handleExcluirNivel}
                  loading={loadingHier}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      {/* Modal Incluir Conta-Salário */}
      <ModalIncluirContaSalario
        show={modalIncluirConta}
        onHide={() => setModalIncluirConta(false)}
        onSuccess={handleSuccessIncluirConta}
        codigoGrupoEmpresa={grupoEmpresa ? grupoEmpresa.value : null}
        apelidoGrupoEmpresa={grupoEmpresa ? grupoEmpresa.label : ''}
      />

      {/* Modal Suplente */}
      <ModalSuplente
        contaNivel={modalSuplente}
        onClose={() => setModalSuplente(null)}
        onSuccess={handleSuccessSuplente}
      />

      {/* Confirmação — exclusão de conta-salário */}
      <ModalConfirmacao
        show={confirmConta.show}
        title="Confirmar Exclusão"
        message={
          confirmConta.item
            ? 'Deseja excluir a conta ' +
              (confirmConta.item.apelido_grupo_empresa || '') +
              ' / ' +
              (confirmConta.item.apelido_conta || '') +
              '?'
            : ''
        }
        onClose={handleConfirmarExclusaoConta}
      />

      {/* Confirmação — exclusão de usuário da hierarquia (com cascata) */}
      <ModalConfirmacao
        show={confirmNivel.show}
        title={confirmNivel.cascade ? 'Atenção' : 'Confirmar Exclusão'}
        message={confirmNivel.message}
        onClose={handleConfirmarExclusaoNivel}
      />
    </Container>
  );
};

export default ContasSalario;
